import React, { useRef, useState } from "react";
import { Download, Upload, X, FileSpreadsheet } from "lucide-react";
import { api } from "../lib/api";
import { downloadProductImportTemplate, parseProductImportFile } from "../lib/productImportTemplate";
import { toast } from "sonner";

export default function BulkProductImportModal({ onClose, onSuccess, storeId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const importFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const rows = await parseProductImportFile(file);
      if (rows.length === 0) {
        toast.error("No valid product rows found — check the template");
        return;
      }
      const { data } = await api.post("/products/import", {
        rows,
        store_id: storeId || undefined,
      });
      setResult(data);
      toast.success(`Imported ${data.created} product${data.created === 1 ? "" : "s"}`);
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Import failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-import-modal"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md grid place-items-center" style={{ background: "var(--surface-tint)", color: "var(--primary)" }}>
              <FileSpreadsheet size={22} />
            </div>
            <div>
              <h2 className="font-display font-bold text-xl">Bulk import products</h2>
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                Upload Excel with sizes, colors, prices &amp; barcodes
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[var(--surface-muted)]">
            <X size={18} />
          </button>
        </div>

        <div className="rounded-lg p-4 mb-4 space-y-3" style={{ background: "var(--surface-muted)" }}>
          <p className="text-sm">
            Download the template, fill one row per variant (e.g. same kurta in M/Red and L/Blue). Leave barcode blank to auto-generate.
          </p>
          <button
            type="button"
            data-testid="bulk-import-download-template"
            onClick={downloadProductImportTemplate}
            className="inline-flex items-center gap-2 px-4 h-11 rounded-md font-semibold border w-full justify-center"
            style={{ borderColor: "var(--primary)", color: "var(--primary)" }}
          >
            <Download size={18} /> Download Excel template
          </button>
        </div>

        <label
          data-testid="bulk-import-upload-label"
          className={`flex flex-col items-center justify-center gap-2 h-32 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${busy ? "opacity-60 pointer-events-none" : ""}`}
          style={{ borderColor: "var(--border-soft)" }}
        >
          <Upload size={24} style={{ color: "var(--primary)" }} />
          <span className="text-sm font-semibold">{busy ? "Importing..." : "Click to upload .xlsx file"}</span>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>Use the template format</span>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            data-testid="bulk-import-file-input"
            onChange={(e) => importFile(e.target.files?.[0])}
          />
        </label>

        {result && (
          <div className="mt-4 p-4 rounded-lg text-sm" style={{ background: "var(--surface-success)", color: "var(--success)" }}>
            <div className="font-semibold">Import complete</div>
            <div>Created: {result.created}</div>
            {result.skipped?.length > 0 && (
              <div className="mt-2">
                <div className="font-medium">Skipped ({result.skipped.length}):</div>
                <ul className="list-disc pl-5 mt-1 space-y-0.5">
                  {result.skipped.slice(0, 5).map((s, i) => (
                    <li key={i}>{s.name}: {s.reason}</li>
                  ))}
                  {result.skipped.length > 5 && <li>…and {result.skipped.length - 5} more</li>}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end mt-5">
          <button type="button" onClick={onClose} className="h-11 px-5 rounded-md border" style={{ borderColor: "var(--border-soft)" }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
